import { useNavigate } from "react-router-dom";
import { History } from "lucide-react";
import { Card, IconButton, Screen, SectionTitle } from "@/components/kit";
import { ModeInputs } from "@/components/find/ModeInputs";
import { Results } from "@/components/find/Results";
import { VeederScan } from "@/components/VeederScan";
import { useSession } from "@/context/SessionContext";
import { useT } from "@/lib/i18n";
import { ShortDisclaimer } from "@/components/Disclaimer";
import { useSimple, setSimple } from "@/lib/simple";
import { StickCheck } from "@/components/find/StickCheck";
import { WelcomeCard } from "@/components/WelcomeCard";
import { HomeIntro } from "@/components/HomeIntro";

const STICK = { label: "Stick only", sub: "Store closed? Pick the tank, type your stick, and see what's in it." };
const SIMPLE_MODES = {
  stick: STICK,
  level: { label: "Stick + console gallons", sub: "Stick the tank, then type the gallons the console shows." },
  receipt: { label: "Console printout", sub: "Copy height, volume and ullage from the printout." },
  drop: { label: "Before & after drop", sub: "Stick before, stick after, and the gallons you dropped." },
};
const SIMPLE_ORDER = ["stick", "level", "receipt", "drop"];

const MODES = [
  { id: "stick", ...STICK },
  { id: "drop", label: "Before & after", sub: "Start stick, end stick, and gallons delivered." },
  { id: "receipt", label: "From receipt", sub: "Height, volume and ullage off the console printout." },
  { id: "level", label: "In the tank", sub: "One stick reading + the gallons the console shows." },
  { id: "ullage", label: "Ullage", sub: "Stick height + empty gallons on the console." },
  { id: "line", label: "Fill line", sub: "Max fill + ullage → the fuel inches." },
  { id: "volume", label: "To inches", sub: "Gallons + tank size → the stick inches." },
];

export default function FindPage() {
  const { session: s, patch, applyTank } = useSession();
  const nav = useNavigate();
  const t = useT();
  const simple = useSimple();
  const modes = simple
    ? MODES.filter((m) => SIMPLE_MODES[m.id] || m.id === s.solverMode).map((m) => ({ ...m, ...SIMPLE_MODES[m.id] }))
      .sort((a, b) => (SIMPLE_ORDER.indexOf(a.id) + 9) % 9 - (SIMPLE_ORDER.indexOf(b.id) + 9) % 9)
    : MODES;
  const mode = modes.find((m) => m.id === s.solverMode);
  const use = (t, start, drop) => { applyTank(t.capacity, t.diameter, t.ends, start, drop); nav("/drop"); };
  const onScan = (plan) => {
    patch(plan.tab === "solver" ? { ...plan.patch, solverMode: "level" } : plan.patch);
    if (plan.tab === "calc") setTimeout(() => nav("/drop"), 1400);
  };

  return (
    <Screen
      testID="find-screen"
      title={simple ? t("Check a tank") : "Fuel Hauler Pro"}
      subtitle={simple ? t("Find which tank it is and how much is in it") : t("Find the tank, then the stick after the drop")}
      right={simple ? null : <IconButton testID="open-sites-find" label={t("Sites")} onPress={() => nav("/sites")}><History size={20} /></IconButton>}
    >
      <WelcomeCard />
      <HomeIntro />
      {s.solverMode !== "stick" && <VeederScan onApply={onScan} />}
      <Card testID="solver-card">
        <SectionTitle>{simple ? t("What do you have?") : t("Find the tank")}</SectionTitle>
        <div className="mode-row" data-testid="solver-mode-row">
          {modes.map((m) => (
            <button key={m.id} type="button" data-testid={`mode-${m.id}`}
              className={`mode-chip ${simple ? "mode-chip-big" : ""} ${s.solverMode === m.id ? "mode-chip-active" : ""}`}
              onClick={() => patch({ solverMode: m.id })}>
              {t(m.label)}
            </button>
          ))}
        </div>
        <p className="sub" data-testid="mode-sub">{t(mode.sub)}</p>
        {s.solverMode === "stick" ? <StickCheck /> : <><ModeInputs s={s} patch={patch} /><Results s={s} use={use} /></>}
      </Card>
      {s.solverMode === "stick" && <VeederScan onApply={onScan} />}
      <ShortDisclaimer testID="find-disclaimer" />
      {!simple && (
        <button type="button" className="ghost-btn simple-switch" data-testid="switch-simple" onClick={() => setSimple(true)}>{t("Switch to simple mode")}</button>
      )}
    </Screen>
  );
}
